/**
 * THE shared 409 wrapper for every hire recompute call (extend / shorten /
 * change-start). Task 5's `resolveAdjustmentDecision`
 * (server/routes/hires.js) refuses one of those PATCHes with
 * `409 { error: 'adjustments_exist', adjustments: [...] }` when the item
 * already carries an attributed hire-price adjustment and the caller sent
 * no `adjustment_decision` — ⚖️ Pete, 2026-09-01: "go with B, but give the
 * person the option to keep the overide or remove it." A recompute that
 * silently kept or silently deleted the adjustment would both be wrong; the
 * 409 exists so a human decides.
 *
 * 🚫 CODE UNIFICATION PRINCIPLE — ONE wrapper, every recompute call site.
 * Do not re-inline this `if (res.status !== 409) …` shape at a call site;
 * import this instead (root CLAUDE.md, "NEVER DUPLICATE — ALWAYS IMPORT").
 *
 * `patchHireWithAdjustmentDecision(apiPatch, url, body, { onNeedsDecision })`
 * → `Promise<Response-shaped>` (only `.ok` / `.status` / `.json()` are used
 * anywhere in this codebase's hires pages, so that is all this returns —
 * see the shim note below).
 *
 * - A non-409 response (200, or any OTHER error status) is returned
 *   UNTOUCHED, in exactly one request. `onNeedsDecision` is never called.
 * - A 409 whose body is NOT `{ error: 'adjustments_exist', … }` (some other
 *   conflict) is ALSO returned untouched and never retried — this wrapper
 *   owns exactly one 409 shape, not "409" in general.
 * - A 409 `adjustments_exist` calls `onNeedsDecision(adjustments)`, which
 *   resolves `'keep' | 'remove' | null`:
 *     - `'keep'` / `'remove'` → resend the SAME url with
 *       `{ ...body, adjustment_decision: decision }`, exactly once more,
 *       and resolve with THAT response. The original `body` object is
 *       never mutated — a caller closing over it for anything else (audit
 *       messages, a second read) must see it unchanged.
 *     - `null` (cancel, or `onNeedsDecision` omitted) → resend NOTHING.
 *       Resolve with the ORIGINAL 409, reconstructed from the body already
 *       read (a `Response` body can only be consumed once, so the literal
 *       object cannot be handed back) — callers' EXISTING
 *       `if (!res.ok) { const err = await res.json(); throw new
 *       Error(err.error || '…') }` path runs exactly as it would for any
 *       other refused PATCH. The wrapper itself takes no further action:
 *       no retry, no throw, no toast of its own — "inert" in that sense.
 *
 * 🪤 Every hires page handler here reads only `res.ok` and `res.json()` off
 * what `apiPatch` returns (grep the three pages — none reads `.headers`,
 * `.clone()`, `.text()` or anything else), so a plain object satisfies every
 * caller; no real `Response`/`fetch` polyfill is required in a test.
 */
export async function patchHireWithAdjustmentDecision(apiPatch, url, body, { onNeedsDecision } = {}) {
  const res = await apiPatch(url, body);
  if (res.status !== 409) return res;

  let payload;
  try {
    payload = await res.json();
  } catch (e) {
    // Not a JSON 409 body at all — nothing this wrapper understands; hand
    // the real response back untouched rather than guessing.
    return res;
  }

  if (!payload || payload.error !== 'adjustments_exist') {
    return jsonResponse(res.status, payload);
  }

  const decision = onNeedsDecision ? await onNeedsDecision(payload.adjustments || []) : null;

  if (decision !== 'keep' && decision !== 'remove') {
    return jsonResponse(409, payload);
  }

  return apiPatch(url, { ...body, adjustment_decision: decision });
}

/**
 * THE Gantt drag → route map (2026-09-28): `change_start` → `/change-start`
 * `{ new_hire_from }`, `extend` → `/extend` `{ new_hire_to }`, anything else →
 * `/shorten` `{ new_hire_to }`, each through the 409 wrapper above. Resolves
 * on `res.ok`; throws `Error(err.error || '<fallback>')` otherwise — the shape
 * every page's `onDragComplete` already had.
 * 🔴 Extracted because `AdminHires.js` carried a copy with NO `change_start`
 * branch, so a start-date drag there PATCHed `/shorten` with the new START
 * date as the new END date. `DepartmentHires.js` and `MyHires.js` still carry
 * their own (correct) inline copies — owed: adopt this helper there.
 */
export async function patchHireDrag(apiPatch, item, newDateStr, action, { onNeedsDecision } = {}) {
  const [path, body, fallback] = action === 'change_start'
    ? ['change-start', { new_hire_from: newDateStr }, 'Failed to change start date']
    : action === 'extend'
      ? ['extend', { new_hire_to: newDateStr }, 'Failed to extend hire']
      : ['shorten', { new_hire_to: newDateStr }, 'Failed to shorten hire'];
  const res = await patchHireWithAdjustmentDecision(apiPatch, `/api/hires/${item.item_id}/${path}`, body, { onNeedsDecision });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(hireRefusalMessage(err, fallback));
  }
  return res;
}

/** What a single-line hire action says when someone else moved the line first. */
export const HIRE_CHANGED_ELSEWHERE_MESSAGE = 'Someone else changed this hire since you opened it. Reload to see where it stands now.';

// The single-line routes' "the line is no longer in the state you saw" 409s
// (2026-09-28): extend on a line already returned, re-hire on a line already
// back on hire.
const CHANGED_ELSEWHERE_CODES = new Set(['already_off_hired', 'not_off_hired']);

/**
 * THE message for a refused single-line hire PATCH body: the reload message
 * for a changed-elsewhere 409, else the server's own words, else `fallback`.
 * Every hires page's extend / off-hire / re-hire / drag handler throws this.
 */
export function hireRefusalMessage(payload, fallback) {
  if (payload && CHANGED_ELSEWHERE_CODES.has(payload.code)) return HIRE_CHANGED_ELSEWHERE_MESSAGE;
  return (payload && payload.error) || fallback;
}

function jsonResponse(status, payload) {
  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => payload,
  };
}
